import { useNavigate } from '@tanstack/react-router';
import { useRef } from 'react';
import { DialogTrigger, MenuTrigger, Toolbar } from 'react-aria-components';

import { Button, Checkbox, Menu, MenuItem, Popover, Separator, Text } from '@/components/core';
import {
  ChevronIcon,
  CircleUpRightIcon,
  CloseIcon,
  DeleteIcon,
  DownloadIcon,
  ReviewIcon,
  RotateIcon,
} from '@/components/icons';
import { ExportMenu } from '@/components/ui';
import { useInboxStore } from '@/store';

import { DeleteAgentRunsModal } from './DeleteAgentRunsModal';
import { useRerunCallbacks } from './rerun';
import { RerunAgentRunsModal } from './RerunAgentRunsModal';

export type InboxSelectedToolbarProps = {
  agentId: string;
};

export const InboxSelectedToolbar = ({ agentId }: InboxSelectedToolbarProps) => {
  const selectedRunIds = useInboxStore((state) => state.selectedRunIds);
  const setSelectedRunIds = useInboxStore((state) => state.setSelectedRunIds);
  const addFilter = useInboxStore((state) => state.addFilter);
  const triggerRef = useRef(null);
  const navigate = useNavigate();

  const { onRerunFromStart, onRerunFromExport, progress, isPending } = useRerunCallbacks({
    agentId,
    selectedRunIds,
    onComplete: () => {
      setSelectedRunIds([]);
    },
  });

  return (
    <div className="group/runs-toolbar absolute bottom-0 left-1/2">
      <div ref={triggerRef} />
      <Popover
        /* Override zIndex to make sure toolbar stays below modals */
        className="!z-10 border-gray-300 shadow-md"
        offset={24}
        triggerRef={triggerRef}
        isOpen={selectedRunIds.size > 0}
        placement="top"
        isNonModal={true}
      >
        <Toolbar orientation="horizontal" className="gap-sm p-sm flex h-10 items-center">
          <Checkbox isDisabled={true} isSelected={true} />
          <Text className="font-semibold">{selectedRunIds.size} runs</Text>

          <Separator orientation="vertical" />

          <Button
            variant="plain"
            onPress={async () => {
              addFilter({
                type: 'string',
                columnName: 'runId',
                condition: 'is',
                value: Array.from(selectedRunIds),
              });
              return navigate({
                to: '/details/agents/$agentId/runs/$runId',
                params: { agentId: agentId, runId: Array.from(selectedRunIds)[0] },
                search: { showPanel: 'agent-runs' },
              });
            }}
          >
            <ReviewIcon />
            <Text>Review</Text>
          </Button>

          <Separator orientation="vertical" />

          <ExportMenu agentId={agentId} runIds={Array.from(selectedRunIds)}>
            <Button variant="plain">
              <DownloadIcon />
              <Text>Export</Text>
            </Button>
          </ExportMenu>

          <Separator orientation="vertical" />

          <MenuTrigger>
            <Button variant="plain">
              <RotateIcon rotation="cw" />
              <Text>Rerun</Text>
              <ChevronIcon rotation="down" />
            </Button>

            <RerunAgentRunsModal progress={progress} isOpen={isPending} />

            <Popover className="group/runs-toolbar">
              <Menu>
                <MenuItem onAction={onRerunFromStart}>
                  <Button variant="plain">
                    <RotateIcon rotation="cw" />
                    <Text>Rerun from start</Text>
                  </Button>
                </MenuItem>

                <MenuItem onAction={onRerunFromExport}>
                  <Button variant="plain">
                    <CircleUpRightIcon />
                    <Text>Rerun export</Text>
                  </Button>
                </MenuItem>
              </Menu>
            </Popover>
          </MenuTrigger>

          <Separator orientation="vertical" />

          <DialogTrigger>
            <Button variant="error">
              <DeleteIcon />
              <Text>Delete</Text>
            </Button>
            <DeleteAgentRunsModal
              agentId={agentId}
              runIds={Array.from(selectedRunIds)}
              onClose={() => setSelectedRunIds([])}
            />
          </DialogTrigger>

          <Separator orientation="vertical" />

          <Button variant="plain" onPress={() => setSelectedRunIds([])}>
            <CloseIcon />
          </Button>
        </Toolbar>
      </Popover>
    </div>
  );
};
